import { ILogger } from '@qest/logger-utils'
import * as SentryTypes from '@sentry/minimal'
import { Severity } from '@sentry/types/dist/severity'
import { ShopConfig } from '~/module/shopConfig/shopConfigPlugin'
import { SEVERITY_LEVELS } from '~/module/logger/index'
import { isSeznamMapError } from '~/utils/logger/loggerHelper'

export default class FrontendLogger implements ILogger {
  sentry: typeof SentryTypes
  shopConfig: ShopConfig

  constructor(sentry: typeof SentryTypes, shopConfig: ShopConfig) {
    this.sentry = sentry
    this.shopConfig = shopConfig
  }

  debug(msg: string | object | Error, ...args: any[]) {
    console.debug(msg, args)

    this.sentryLog({
      args,
      msg,
      type: Severity.Debug,
    })
  }

  error(msg: string | object | Error, ...args: any[]) {
    console.debug(msg, args)

    this.sentryLog({
      args,
      msg,
      type: Severity.Error,
    })
  }

  fatal(msg: string | object | Error, ...args: any[]) {
    console.error(msg, args)

    this.sentryLog({
      args,
      msg,
      type: Severity.Fatal,
    })
  }

  info(msg: string | object | Error, ...args: any[]) {
    console.info(msg, args)

    this.sentryLog({
      args,
      msg,
      type: Severity.Info,
    })
  }

  trace(msg: string | object | Error, ...args: any[]) {
    console.trace(msg, args)

    this.sentryLog({
      args,
      msg,
      type: Severity.Info,
    })
  }

  warn(msg: string | object | Error, ...args: any[]) {
    console.warn(msg, args)

    this.sentryLog({
      args,
      msg,
      type: Severity.Warning,
    })
  }

  sentryLog(log: {
    msg: string | object | Error
    args: any[]
    type: Severity
  }): void {
    const { msg } = log
    this.sentry &&
      this.sentry.withScope((scope) => {
        const logSeverity = SEVERITY_LEVELS[log.type]
        if (logSeverity < SEVERITY_LEVELS[Severity.Warning]) {
          return
        }
        if (isSeznamMapError(msg)) {
          return
        }

        scope.setExtra('args', log.args)
        scope.setExtra('user', this.userInfo)
        scope.setExtra('flattenLog', this.flattenObject(log))

        if (msg.constructor === Error) {
          return this.sentry.captureException(msg)
        }

        this.sentry.captureMessage(log.msg as string, log.type)
      })
  }

  get userInfo() {
    const { isBot, user } = this.shopConfig || {}
    return {
      isBot,
      userId: user?.id,
    }
  }

  flattenObject(ob: any) {
    try {
      const toReturn: Record<string, object> = {}
      for (const iteratorOb in ob) {
        // eslint-disable-next-line no-prototype-builtins
        if (!ob.hasOwnProperty(iteratorOb)) {
          continue
        }
        if (typeof ob[iteratorOb] === 'object' && ob[iteratorOb] !== null) {
          const flatObject = this.flattenObject(ob[iteratorOb])
          for (const iteratorObFlat in flatObject) {
            // eslint-disable-next-line no-prototype-builtins
            if (!flatObject.hasOwnProperty(iteratorObFlat)) {
              continue
            }
            toReturn[`${iteratorOb}.${iteratorObFlat}`] =
              flatObject[iteratorObFlat]
          }
        } else {
          toReturn[iteratorOb] = ob[iteratorOb]
        }
      }
      return toReturn
    } catch (e) {
      return null
    }
  }
}
